import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Phone, Linkedin, Facebook, Youtube, Instagram } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import type { RouteKey } from '@/i18n/routes'

interface NavItem {
  label: string
  routeKey: RouteKey
}

interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
  navItems: NavItem[]
  isActive: (routeKey: RouteKey) => boolean
}

const socialLinks = [
  { icon: Linkedin, href: 'https://www.linkedin.com/company/life-elettronica/', label: 'LinkedIn' },
  { icon: Facebook, href: 'https://www.facebook.com/lifeelettronica', label: 'Facebook' },
  { icon: Youtube, href: 'https://www.youtube.com/@lifeelettronica', label: 'YouTube' },
  { icon: Instagram, href: 'https://www.instagram.com/lifeelettronica/', label: 'Instagram' },
]

export function MobileMenu({ isOpen, onClose, navItems, isActive }: MobileMenuProps) {
  const { t } = useTranslation('common')

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[999] bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 z-[1001] w-[85vw] max-w-sm bg-white shadow-2xl lg:hidden"
          >
            <div className="flex h-full flex-col">
              {/* Nav Links */}
              <nav className="flex-1 overflow-y-auto px-6 pt-24 pb-6">
                <ul className="space-y-1">
                  {navItems.map((item, index) => (
                    <motion.li
                      key={item.routeKey}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + index * 0.05 }}
                    >
                      <LocalizedLink
                        to={item.routeKey}
                        onClick={onClose}
                        className={`block px-4 py-3.5 text-lg font-medium rounded-xl transition-colors ${
                          isActive(item.routeKey)
                            ? 'text-primary-600 bg-primary-50'
                            : 'text-gray-800 hover:bg-gray-50'
                        }`}
                      >
                        {item.label}
                      </LocalizedLink>
                    </motion.li>
                  ))}
                </ul>

                {/* CTA Button */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-6"
                >
                  <LocalizedLink
                    to="contacts"
                    onClick={onClose}
                    className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-primary-400 text-ink font-semibold rounded-full hover:bg-primary-300 transition-colors shadow-lg shadow-primary-500/25"
                  >
                    <Phone className="h-4 w-4" />
                    {t('buttons.contactUs')}
                  </LocalizedLink>
                </motion.div>
              </nav>

              {/* Bottom Section */}
              <div className="border-t border-gray-100 px-6 py-5 space-y-4">
                {/* Language Selector */}
                <LanguageSelector variant="mobile" />

                {/* Social Links */}
                <div className="flex items-center gap-3 px-4">
                  {socialLinks.map(({ icon: Icon, href, label }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                      aria-label={label}
                    >
                      <Icon className="h-5 w-5" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
